/**
 * Utility functions for handling permission implications, forbidden
 * combinations, and UI state.
 */

export type PermissionState = "unchecked" | "checked" | "implied" | "forbidden";

export interface PermissionRules {
  impliedPermissions: Record<number, number[]>;
  forbiddenPermissions: Record<number, number[]>;
}

/**
 * Check if `bit` is forbidden by any currently selected permission.
 * Forbidden rules are bidirectional: if A forbids B, then B forbids A.
 */
function isForbidden(
  bit: number,
  selectedBits: Set<number>,
  forbiddenPermissions: Record<number, number[]>,
): boolean {
  for (const [source, targets] of Object.entries(forbiddenPermissions)) {
    const sourceBit = Number(source);
    if (sourceBit === bit && targets.some((t) => selectedBits.has(t)))
      return true;
    if (targets.includes(bit) && selectedBits.has(sourceBit)) return true;
  }
  return false;
}

/**
 * Get the state of a permission bit based on current selections.
 */
export function getPermissionState(
  bit: number,
  selectedBits: Set<number>,
  rules: PermissionRules,
): PermissionState {
  if (!selectedBits.has(bit)) {
    if (isForbidden(bit, selectedBits, rules.forbiddenPermissions))
      return "forbidden";
    return "unchecked";
  }

  // Check if this permission is implied by any other selected permission
  for (const [parentBit, impliedBits] of Object.entries(
    rules.impliedPermissions,
  )) {
    const parentBitNum = Number(parentBit);
    if (selectedBits.has(parentBitNum) && impliedBits.includes(bit)) {
      return "implied";
    }
  }

  return "checked";
}

/**
 * True for states the user can't toggle directly: `implied` (auto-selected by a
 * parent) and `forbidden` (blocked by a conflict). These rows are shown
 * de-emphasized with a disabled selection checkbox.
 */
export function isDerivedState(state: PermissionState): boolean {
  return state === "implied" || state === "forbidden";
}

/**
 * Handle permission selection: adds the permission, its implied permissions,
 * and removes any forbidden permissions.
 */
export function addPermissionWithImplied(
  bit: number,
  currentBits: number[],
  rules: PermissionRules,
): number[] {
  const newBits = new Set(currentBits);

  newBits.add(bit);

  // Add all implied permissions
  const impliedBits = rules.impliedPermissions[bit] || [];
  impliedBits.forEach((impliedBit) => newBits.add(impliedBit));

  // Remove bits that conflict with what was just added
  const addedBits = new Set([bit, ...impliedBits]);
  for (const existing of [...newBits]) {
    if (addedBits.has(existing)) continue;
    if (isForbidden(existing, addedBits, rules.forbiddenPermissions)) {
      newBits.delete(existing);
      // Also remove parents that imply the removed bit
      for (const [parentBit, implied] of Object.entries(
        rules.impliedPermissions,
      )) {
        if (implied.includes(existing)) {
          newBits.delete(Number(parentBit));
        }
      }
    }
  }

  return Array.from(newBits);
}

/**
 * Handle permission deselection by removing the permission and any
 * permissions that depend on it.
 */
export function removePermissionWithDependents(
  bit: number,
  currentBits: number[],
  rules: PermissionRules,
): number[] {
  const newBits = new Set(currentBits);

  newBits.delete(bit);

  // Remove any permissions that depend on this one
  for (const [parentBit, impliedBits] of Object.entries(
    rules.impliedPermissions,
  )) {
    if (impliedBits.includes(bit)) {
      newBits.delete(Number(parentBit));
    }
  }

  return Array.from(newBits);
}
